import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Col,
  Row,
  Typography,
  Checkbox,
  Space,
  Drawer,
  Button,
  Spin,
  Empty,
  Input
} from 'antd';
import { FilterOutlined, LoadingOutlined, UpOutlined, DownOutlined, LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { ProductCard } from '@components/molecules/ProductCard/ProductCard';
import { ContactExpertsModal } from '@components/molecules';
import { useProducts, useAuth } from '@hooks';
import './styles.css';

// Importe os estilos do Swiper
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';

// --- Dados dos Produtos para o Carrossel ---
interface CarouselProduct {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
}

const carouselProducts: CarouselProduct[] = [
  { id: 1, name: 'Ladder Chain', description: 'LEARN MORE', imageUrl: '/assets/images/LadderChainHook.png' },
  { id: 2, name: 'Revo Hook', description: 'LEARN MORE', imageUrl: '/assets/images/RevoHook.png' },
  { id: 3, name: 'Basket Chain', description: 'LEARN MORE', imageUrl: '/assets/products/SC_0_27.png' },
];

const loopedCarouselProducts = [...carouselProducts, ...carouselProducts];

// --- Dados Base dos Filtros (serão mesclados com dados dos produtos) ---
const BASE_VEHICLE_TYPE_OPTIONS = [
  { label: 'Trackless', value: 'Trackless' },
  { label: 'Trucks & Busses', value: 'Trucks and Busses' },
  { label: 'Heavy Equipment', value: 'Heavy Equipment' },
];

const BASE_CHAINS_OPTIONS = [
  { label: 'Bulk Cross Chain', value: 'Bulk Cross Chain' },
  { label: 'Bulk Side Chain', value: 'Bulk Side Chain' },
  { label: 'Completed Cross Chain', value: 'Completed Cross Chain' },
];

const BASE_HARDWARE_PARTS_OPTIONS = [
  { label: 'Fasteners', value: 'Fasteners' },
  { label: 'Hooks', value: 'Hooks' },
  { label: 'Repair Links & Shackles', value: 'Repair Links & Shackles' },
  { label: 'Tensioners', value: 'Tensioners' },
  { label: 'Chain Tools', value: 'Chain Tools' },
];

const BASE_CHAIN_SIZE_OPTIONS = [
  { label: '7mm', value: '7mm' },
  { label: '8mm', value: '8mm' },
  { label: '9.5mm', value: '9.5mm' },
  { label: '11mm', value: '11mm' },
  { label: '12mm', value: '12mm' },
  { label: '14mm', value: '14mm' },
];

const BASE_TIRE_SIZE_OPTIONS = [
  { label: '14-17.5', value: '14-17.5' },
  { label: '17.5-25', value: '17.5-25' },
  { label: '20.5-25', value: '20.5-25' },
  { label: '23.5-25', value: '23.5-25' },
  { label: '26.5-25', value: '26.5-25' },
  { label: '29.5-25', value: '29.5-25' },
  { label: '17.5-24', value: '17.5-24' },
  { label: '12.5/80-18', value: '12.5/80-18' },
  { label: '11-24.5', value: '11-24.5' },
  { label: '11-22.5', value: '11-22.5' },
  { label: '225/75-16', value: '225/75-16' },
  { label: '365/80-20', value: '365/80-20' },
];

// Categorias que correspondem a cada tipo de filtro
const VEHICLE_TYPE_CATEGORIES = ['Trackless', 'Trucks and Busses', 'Heavy Equipment', 'Heavy Machinery'];
const CHAINS_CATEGORIES = ['Bulk Cross Chain', 'Bulk Side Chain', 'Completed Cross Chain'];
const HARDWARE_PARTS_CATEGORIES = ['Fasteners', 'Hooks', 'Repair Links & Shackles', 'Tensioners', 'Chain Tools', 'Parts & Accessories', 'Safety Gear', 'Tools'];

// Tipo para opções de filtro
interface FilterOption {
  label: string;
  value: string;
}

// Função para normalizar valores de tamanho para comparação (remove TODOS os espaços e converte para lowercase)
// Isso garante que "7mm", "7 mm", "7  MM" sejam tratados como iguais
const normalizeSizeValue = (value: string): string => {
  return value.replace(/\s+/g, '').toLowerCase();
};

// Função para normalizar categorias para comparação
// - Faz trim nas bordas
// - Colapsa múltiplos espaços internos em um único espaço
// - Normaliza "&" para "and" para consistência
// - Converte para lowercase
// Isso garante que "Trucks and Busses", "Trucks & Busses", "Trucks  and  Busses" sejam tratados como iguais
const normalizeCategory = (value: string): string => {
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*&\s*/g, ' and ')
    .toLowerCase();
};

// Função para verificar se uma categoria está em uma lista (comparação normalizada)
const categoryMatches = (category: string, list: string[]): boolean => {
  const normalizedCategory = normalizeCategory(category);
  return list.some(item => normalizeCategory(item) === normalizedCategory);
};

// Função para verificar se uma categoria está nas opções base (comparação normalizada)
const categoryMatchesOptions = (category: string, options: FilterOption[]): boolean => {
  const normalizedCategory = normalizeCategory(category);
  return options.some(opt => normalizeCategory(opt.value) === normalizedCategory);
};

// Função para mesclar opções de tamanho (remove todos os espaços para comparação)
const mergeSizeFilterOptions = (baseOptions: FilterOption[], productValues: string[]): FilterOption[] => {
  const mergedOptions = [...baseOptions];
  const seenNormalizedValues = new Set(baseOptions.map(opt => normalizeSizeValue(opt.value)));
  
  productValues.forEach(value => {
    if (!value) return;
    
    const trimmedValue = value.trim();
    const normalizedValue = normalizeSizeValue(trimmedValue);
    
    // Só adiciona se o valor normalizado ainda não existe
    if (!seenNormalizedValues.has(normalizedValue)) {
      seenNormalizedValues.add(normalizedValue);
      mergedOptions.push({ label: trimmedValue, value: trimmedValue });
    }
  });
  
  return mergedOptions;
};

// Função para mesclar opções de categoria (mantém espaços internos, mas normaliza múltiplos espaços)
const mergeCategoryFilterOptions = (baseOptions: FilterOption[], productValues: string[]): FilterOption[] => {
  const mergedOptions = [...baseOptions];
  // Mapa de valor normalizado -> valor original da base option
  const normalizedToBaseValue = new Map<string, string>();
  baseOptions.forEach(opt => {
    normalizedToBaseValue.set(normalizeCategory(opt.value), opt.value);
  });
  const seenNormalizedValues = new Set(normalizedToBaseValue.keys());
  
  productValues.forEach(value => {
    if (!value) return;
    
    // Normaliza o valor (trim + colapsa espaços múltiplos)
    const cleanedValue = value.trim().replace(/\s+/g, ' ');
    const normalizedValue = normalizeCategory(cleanedValue);
    
    // Só adiciona se o valor normalizado ainda não existe
    if (!seenNormalizedValues.has(normalizedValue)) {
      seenNormalizedValues.add(normalizedValue);
      mergedOptions.push({ label: cleanedValue, value: cleanedValue });
    }
  });
  
  return mergedOptions;
};

// Função para mapear um valor de categoria do produto para o valor correspondente da base option
// Isso garante que ao filtrar, usamos valores consistentes
const getCanonicalCategoryValue = (productCategory: string, baseOptions: FilterOption[]): string => {
  const normalizedProductCategory = normalizeCategory(productCategory);
  
  // Procura nas base options por uma correspondência normalizada
  for (const opt of baseOptions) {
    if (normalizeCategory(opt.value) === normalizedProductCategory) {
      return opt.value; // Retorna o valor da base option
    }
  }
  
  // Se não encontrou, retorna o valor limpo do produto
  return productCategory.trim().replace(/\s+/g, ' ');
};

// --- Componente de Filtro Colapsável ---
interface CollapsibleFilterProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleFilter = ({ title, isOpen, onToggle, children }: CollapsibleFilterProps) => (
  <div className="mb-4">
    {/* Header with white background */}
    <div 
      className="flex items-center justify-between px-4 py-3 cursor-pointer bg-white border-4 border-white"
      onClick={onToggle}
    >
      <span className="font-orator text-black uppercase tracking-wider text-sm font-semibold">
        {title}
      </span>
      {isOpen ? (
        <UpOutlined className="text-black/50 text-xs" />
      ) : (
        <DownOutlined className="text-black/50 text-xs" />
      )}
    </div>
    
    {/* Content box with clipPath - only visible when open */}
    {isOpen && (
      <div
        className="relative border-4 border-white border-t-0 transition-all duration-300"
        style={{ clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)' }}
      >
        {/* Corner decoration - top left triangle */}
        <div
          className="pointer-events-none absolute top-0 left-0 bg-white z-10"
          style={{ width: 20, height: 20, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
        />
        {/* Corner decoration - bottom right triangle */}
        <div
          className="pointer-events-none absolute bottom-0 right-0 bg-white"
          style={{ width: 20, height: 20, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
        />
        
        <div className="px-4 pb-4 pt-4">
          {children}
        </div>
      </div>
    )}
  </div>
);


const { Title, Paragraph } = Typography;

// --- Props para o Componente de Filtro ---
interface FilterState {
  vehicleType: string[];
  chains: string[];
  hardwareParts: string[];
  chainSize: string[];
  tireSize: string[];
}

interface FilterContentProps {
  filters: FilterState;
  onFilterChange: (filterType: keyof FilterState, values: string[]) => void;
  openFilters: { [key: string]: boolean };
  onToggleFilter: (filterKey: string) => void;
  filterOptions: {
    vehicleType: FilterOption[];
    chains: FilterOption[];
    hardwareParts: FilterOption[];
    chainSize: FilterOption[];
    tireSize: FilterOption[];
  };
}

// --- Lista de checkboxes com busca e altura limitada ---
// Usada em filtros que podem ter centenas de opções (ex.: Tire Size), evitando
// que a página cresça indefinidamente com os valores vindos do banco.
interface SearchableCheckboxGroupProps {
  options: FilterOption[];
  value: string[];
  onChange: (values: string[]) => void;
  searchPlaceholder?: string;
}

const SearchableCheckboxGroup = ({ options, value, onChange, searchPlaceholder = 'Search...' }: SearchableCheckboxGroupProps) => {
  const [search, setSearch] = useState('');
  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = normalizedSearch
    ? options.filter(option => option.label.toLowerCase().includes(normalizedSearch))
    : options;

  return (
    <div>
      <Input
        allowClear
        prefix={<SearchOutlined className="text-black/40" />}
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="font-orator filter-search"
      />
      <div className="max-h-72 overflow-y-auto pr-2 filter-scroll w-full">
        <Checkbox.Group
          className="w-full"
          value={value}
          onChange={(values) => onChange(values as string[])}
        >
          {filteredOptions.length > 0 ? (
            <Space direction="vertical" className="w-full">
              {filteredOptions.map(option => (
                <Checkbox key={option.value} value={option.value} className="font-orator filter-checkbox">
                  {option.label}
                </Checkbox>
              ))}
            </Space>
          ) : (
            <span className="font-orator text-white/50 text-sm">No matches</span>
          )}
        </Checkbox.Group>
      </div>
    </div>
  );
};

// --- Componente de Filtro Atualizado ---
const FilterContent = ({ filters, onFilterChange, openFilters, onToggleFilter, filterOptions }: FilterContentProps) => (
  <div className="space-y-0">
    {/* Product Lines Title */}
    <div className="pb-3">
      <span className="font-orator text-white uppercase tracking-wider text-lg font-semibold">
        Product Lines
      </span>
    </div>

    {/* Vehicle Type */}
    <CollapsibleFilter
      title="Vehicle Type"
      isOpen={openFilters.vehicleType}
      onToggle={() => onToggleFilter('vehicleType')}
    >
      <Checkbox.Group 
        className="w-full"
        value={filters.vehicleType}
        onChange={(values) => onFilterChange('vehicleType', values as string[])}
      >
        <Space direction="vertical" className="w-full">
          {filterOptions.vehicleType.map(option => (
            <Checkbox key={option.value} value={option.value} className="font-orator filter-checkbox">
              {option.label}
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
    </CollapsibleFilter>

    {/* Chains */}
    <CollapsibleFilter
      title="Chains"
      isOpen={openFilters.chains}
      onToggle={() => onToggleFilter('chains')}
    >
      <Checkbox.Group 
        className="w-full"
        value={filters.chains}
        onChange={(values) => onFilterChange('chains', values as string[])}
      >
        <Space direction="vertical" className="w-full">
          {filterOptions.chains.map(option => (
            <Checkbox key={option.value} value={option.value} className="font-orator filter-checkbox">
              {option.label}
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
    </CollapsibleFilter>

    {/* Hardware & Parts */}
    <CollapsibleFilter
      title="Hardware & Parts"
      isOpen={openFilters.hardwareParts}
      onToggle={() => onToggleFilter('hardwareParts')}
    >
      <Checkbox.Group 
        className="w-full"
        value={filters.hardwareParts}
        onChange={(values) => onFilterChange('hardwareParts', values as string[])}
      >
        <Space direction="vertical" className="w-full">
          {filterOptions.hardwareParts.map(option => (
            <Checkbox key={option.value} value={option.value} className="font-orator filter-checkbox">
              {option.label}
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
    </CollapsibleFilter>

    {/* Sizes Title */}
    <div className="pt-6 pb-3">
      <span className="font-orator text-white uppercase tracking-wider text-lg font-semibold">
        Sizes
      </span>
    </div>

    {/* Chain Size */}
    <CollapsibleFilter
      title="Chain Size"
      isOpen={openFilters.chainSize}
      onToggle={() => onToggleFilter('chainSize')}
    >
      <SearchableCheckboxGroup
        options={filterOptions.chainSize}
        value={filters.chainSize}
        onChange={(values) => onFilterChange('chainSize', values)}
        searchPlaceholder="Search chain size..."
      />
    </CollapsibleFilter>

    {/* Tire Size */}
    <CollapsibleFilter
      title="Tire Size"
      isOpen={openFilters.tireSize}
      onToggle={() => onToggleFilter('tireSize')}
    >
      <SearchableCheckboxGroup
        options={filterOptions.tireSize}
        value={filters.tireSize}
        onChange={(values) => onFilterChange('tireSize', values)}
        searchPlaceholder="Search tire size..."
      />
    </CollapsibleFilter>
  </div>
);


export const MerchPage = () => {
  const navigate = useNavigate();
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);

  // Fetch products from API
  const { products } = useProducts();
  const { profile: { data: profile } } = useAuth();
  const isAuthenticated = !!profile;

  // --- Estado para os filtros selecionados ---
  const [filters, setFilters] = useState<FilterState>({
    vehicleType: [],
    chains: [],
    hardwareParts: [],
    chainSize: [],
    tireSize: [],
  });

  // --- Estado para controlar quais filtros estão abertos ---
  const [openFilters, setOpenFilters] = useState<{ [key: string]: boolean }>({
    vehicleType: false,
    chains: false,
    hardwareParts: false,
    chainSize: false,
    tireSize: false,
  });

  // --- Handler para atualizar o estado dos filtros ---
  const handleFilterChange = (filterType: keyof FilterState, values: string[]) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: values,
    }));
  };

  // --- Handler para abrir/fechar filtros ---
  const handleToggleFilter = (filterKey: string) => {
    setOpenFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
  };

  const handleProductClick = (productId: string) => {
    navigate(`/merch/${productId}`);
  };

  // Handler para scroll suave para a seção de produtos
  const handleScrollToProducts = () => {
    const productsSection = document.getElementById('products-grid');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  // Helper function to get product image URL
  const getProductImage = (product: any) => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
    
    // Use images array if available (first image)
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (firstImage.startsWith('http')) {
        return firstImage;
      }
      return `https://${bucketName}.s3.${region}.amazonaws.com/${firstImage}`;
    }
    
    // Fallback to legacy imageUrl
    if (product.imageUrl) {
      if (product.imageUrl.startsWith('http')) {
        return product.imageUrl;
      }
      return `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
    }
    
    return `https://placehold.co/400x400/181717/F74902?text=${encodeURIComponent(product.name)}`;
  };

  // Format price
  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numPrice);
  };

  // Get base price (lowest price from variants for "starting at")
  const getBasePrice = (product: any): number => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map((v: any) => Number(v.price) || 0);
      return Math.min(...prices);
    }
    return 0;
  };

  // Calcular opções de filtro dinâmicas baseadas nos produtos
  const filterOptions = React.useMemo(() => {
    if (!products.data || products.data.length === 0) {
      return {
        vehicleType: BASE_VEHICLE_TYPE_OPTIONS,
        chains: BASE_CHAINS_OPTIONS,
        hardwareParts: BASE_HARDWARE_PARTS_OPTIONS,
        chainSize: BASE_CHAIN_SIZE_OPTIONS,
        tireSize: BASE_TIRE_SIZE_OPTIONS,
      };
    }

    // Extrair categorias dos produtos (com trim para remover espaços extras)
    const rawProductCategories = products.data
      .map((p: any) => p.category?.trim())
      .filter(Boolean) as string[];

    // Extrair tamanhos de corrente dos produtos (chainSizes é um array)
    const productChainSizes = products.data
      .flatMap((p: any) => p.chainSizes || [])
      .filter(Boolean) as string[];

    // Extrair tamanhos de pneu dos produtos (sizes é um array)
    const productTireSizes = products.data
      .flatMap((p: any) => p.sizes || [])
      .filter(Boolean) as string[];

    // Todas as base options combinadas para mapeamento canônico
    const allBaseOptions = [
      ...BASE_VEHICLE_TYPE_OPTIONS,
      ...BASE_CHAINS_OPTIONS,
      ...BASE_HARDWARE_PARTS_OPTIONS,
    ];

    // Mapear categorias dos produtos para valores canônicos (das base options quando há match)
    const productCategories = rawProductCategories.map(cat => 
      getCanonicalCategoryValue(cat, allBaseOptions)
    );

    // Categorizar as categorias dos produtos (usando comparação normalizada)
    const vehicleTypeCategories = productCategories.filter(cat => 
      categoryMatches(cat, VEHICLE_TYPE_CATEGORIES) || 
      categoryMatchesOptions(cat, BASE_VEHICLE_TYPE_OPTIONS)
    );
    
    const chainsCategories = productCategories.filter(cat => 
      categoryMatches(cat, CHAINS_CATEGORIES) ||
      categoryMatchesOptions(cat, BASE_CHAINS_OPTIONS)
    );
    
    const hardwarePartsCategories = productCategories.filter(cat => 
      categoryMatches(cat, HARDWARE_PARTS_CATEGORIES) ||
      categoryMatchesOptions(cat, BASE_HARDWARE_PARTS_OPTIONS)
    );

    // Adicionar categorias que não se encaixam em nenhum grupo ao hardwareParts
    const uncategorizedCategories = productCategories.filter(cat => 
      !categoryMatches(cat, VEHICLE_TYPE_CATEGORIES) &&
      !categoryMatches(cat, CHAINS_CATEGORIES) &&
      !categoryMatches(cat, HARDWARE_PARTS_CATEGORIES) &&
      !categoryMatchesOptions(cat, BASE_VEHICLE_TYPE_OPTIONS) &&
      !categoryMatchesOptions(cat, BASE_CHAINS_OPTIONS) &&
      !categoryMatchesOptions(cat, BASE_HARDWARE_PARTS_OPTIONS)
    );

    return {
      vehicleType: mergeCategoryFilterOptions(BASE_VEHICLE_TYPE_OPTIONS, vehicleTypeCategories),
      chains: mergeCategoryFilterOptions(BASE_CHAINS_OPTIONS, chainsCategories),
      hardwareParts: mergeCategoryFilterOptions(BASE_HARDWARE_PARTS_OPTIONS, [...hardwarePartsCategories, ...uncategorizedCategories]),
      chainSize: mergeSizeFilterOptions(BASE_CHAIN_SIZE_OPTIONS, productChainSizes),
      tireSize: mergeSizeFilterOptions(BASE_TIRE_SIZE_OPTIONS, productTireSizes),
    };
  }, [products.data]);

  // Check if any filter is active
  const hasActiveFilters = React.useMemo(() => {
    return (
      filters.vehicleType.length > 0 ||
      filters.chains.length > 0 ||
      filters.hardwareParts.length > 0 ||
      filters.chainSize.length > 0 ||
      filters.tireSize.length > 0
    );
  }, [filters]);

  // Filter products based on selected filters
  const displayProducts = React.useMemo(() => {
    if (!products.data || products.data.length === 0) {
      return [];
    }

    // If no filters are active, return all products
    let filtered = !hasActiveFilters ? products.data : products.data;

    if (hasActiveFilters) {
      // Apply filters:
      // - vehicleType, chains, hardwareParts: todos usam o campo 'category', então são combinados com OR
      // - chainSize, tireSize: usam campos separados (chainSizes, sizes), então são AND com as categorias
      
      // Combinar todas as categorias selecionadas (OR entre vehicleType, chains, hardwareParts)
      const selectedCategories = [
        ...filters.vehicleType,
        ...filters.chains,
        ...filters.hardwareParts,
      ];
      
      // Se há categorias selecionadas, filtrar produtos que tenham a categoria em qualquer uma delas
      // Usa comparação normalizada para lidar com espaços em branco
      if (selectedCategories.length > 0) {
        const normalizedSelectedCategories = selectedCategories.map(normalizeCategory);
        filtered = filtered.filter((product: any) => {
          if (!product.category) return false;
          const normalizedProductCategory = normalizeCategory(product.category);
          return normalizedSelectedCategories.includes(normalizedProductCategory);
        });
      }

      // chainSize - filtra pelo array chainSizes do produto (comparação normalizada)
      if (filters.chainSize.length > 0) {
        const normalizedFilterChainSizes = filters.chainSize.map(normalizeSizeValue);
        filtered = filtered.filter((product: any) => {
          const productChainSizes = product.chainSizes || [];
          // Produto deve ter pelo menos um dos tamanhos selecionados (comparação normalizada)
          // Produtos sem chainSizes definidos são excluídos
          return productChainSizes.some((size: string) => 
            normalizedFilterChainSizes.includes(normalizeSizeValue(size))
          );
        });
      }

      // tireSize - filtra pelo array sizes do produto (comparação normalizada)
      if (filters.tireSize.length > 0) {
        const normalizedFilterTireSizes = filters.tireSize.map(normalizeSizeValue);
        filtered = filtered.filter((product: any) => {
          const productSizes = product.sizes || [];
          // Produto deve ter pelo menos um dos tamanhos selecionados (comparação normalizada)
          // Produtos sem sizes definidos são excluídos
          return productSizes.some((size: string) => 
            normalizedFilterTireSizes.includes(normalizeSizeValue(size))
          );
        });
      }
    }

    // Sort products by price (lowest to highest) based on the "starting at" price
    const sorted = [...filtered].sort((a: any, b: any) => {
      const priceA = getBasePrice(a);
      const priceB = getBasePrice(b);
      return priceA - priceB;
    });

    return sorted;
  }, [products.data, filters, hasActiveFilters]);

  return (
    <>
      <div className="overflow-x-hidden">

        {/* 1. Hero Section */}
        <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat w-full relative h-[400px]">
          <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 z-10">
            <div className="font-displayed text-white text-6xl tracking-wider text-center uppercase pt-12">
              Our Products
            </div>
            <Paragraph className="font-orator text-orangicide text-center tracking-wider md:text-lg lg:!text-xl max-w-3xl pt-4">
              Innovative traction solutions engineered for the toughest winter conditions
            </Paragraph>
          </div>
        </section>

        {/* 2. Carrossel de Produtos */}
        <section id="products" className="bg-black bg-[url(/assets/images/ModlinkSchemPattern.png)] bg-center bg-cover bg-no-repeat pb-24">
          <div className="relative">
            <Swiper
              centeredSlides={true}
              loop={true}
              slidesPerGroup={1}
              navigation={{
                nextEl: '.swiper-button-next-custom',
                prevEl: '.swiper-button-prev-custom',
              }}
              modules={[Navigation]}
              className="product-swiper"
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                720: {
                  slidesPerView: 2,
                  spaceBetween: 50,
                  centeredSlides: false,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 80,
                  centeredSlides: false,
                },
                1280: {
                  slidesPerView: 4,
                  spaceBetween: 90,
                  centeredSlides: false,
                },
              }}
            >
              {loopedCarouselProducts.map((product, index) => (
                <SwiperSlide key={`${product.id}-${index}`} style={{ width: '410px' }}>
                  <div>
                    <ProductCard
                      imageUrl={product.imageUrl}
                      productName={product.name}
                      description={product.description}
                      variant="merch"
                      onClick={handleScrollToProducts}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Botões de Navegação Customizados */}
            <div className="swiper-button-prev-custom absolute top-1/3 -translate-y-1/2 left-1 lg:left-3 z-10 text-gray-300 cursor-pointer">
              <LeftOutlined className="text-[40px] sm:text-[60px] md:text-[80px] lg:text-[100px]" />
            </div>
            <div className="swiper-button-next-custom absolute top-1/3 -translate-y-1/2 right-1 lg:right-3 z-10 text-gray-300 cursor-pointer">
              <RightOutlined className="text-[40px] sm:text-[60px] md:text-[80px] lg:text-[100px]" />
            </div>
          </div>
        </section>

        {/* 3. Filtros e Grade de Produtos */}
        <main id="products-grid" className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-fixed bg-no-repeat">
          <div className="container mx-auto px-4 py-24">
            <div className="lg:hidden text-center mb-8">
              <Button
                type="primary"
                className="btn-orangicide font-orator !text-white tracking-wider"
                icon={<FilterOutlined />}
                size="large"
                onClick={() => setIsFilterDrawerVisible(true)}
              >
                FILTERS
              </Button>
            </div>

            <Row gutter={[48, 48]}>
              {/* Coluna de Filtros Desktop: agora passa as props */}
              <Col className="hidden lg:block" lg={5}>
                <FilterContent 
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  openFilters={openFilters}
                  onToggleFilter={handleToggleFilter}
                  filterOptions={filterOptions}
                />
              </Col>

              {/* Coluna de Produtos: agora usa filteredProducts */}
              <Col xs={24} lg={19}>
                {products.isLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#F74902' }} spin />} />
                  </div>
                ) : products.isError ? (
                  <div className="text-center py-20">
                    <Empty
                      description={
                        <span className="font-orator text-white">
                          Error loading products. Please try again later.
                        </span>
                      }
                    />
                    <Button
                      type="primary"
                      className="btn-orangicide font-orator mt-4"
                      onClick={() => products.refetch()}
                    >
                      TRY AGAIN
                    </Button>
                  </div>
                ) : displayProducts.length === 0 ? (
                  <div className="text-center py-20">
                    <Empty
                      description={
                        <span className="font-orator text-white">
                          No products available at the moment.
                        </span>
                      }
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                    {displayProducts.map((product: any) => (
                      <div key={product.id} className="flex flex-col gap-4 group">
                        {/* Image container with gradient overlay */}
                        <div
                          onClick={() => handleProductClick(product.id)}
                          className="block cursor-pointer product-card-hover relative overflow-hidden"
                        >
                          <div className="w-full h-64 flex items-center justify-center p-4 relative">
                            <img 
                              src={getProductImage(product)} 
                              alt={product.name} 
                              className="max-w-full max-h-full object-contain product-image transition-all duration-300 relative z-10"
                              onError={(e) => {
                                e.currentTarget.src = `https://placehold.co/400x400/181717/F74902?text=${encodeURIComponent(product.name)}`;
                              }}
                            />
                            {/* Gradient overlay - solid at bottom, fading up */}
                            <div 
                              className="absolute inset-0 pointer-events-none z-0"
                              style={{
                                background: 'linear-gradient(to top, rgba(247, 73, 2, 0.5) 0%, rgba(247, 73, 2, 0.2) 40%, rgba(247, 73, 2, 0) 85%)'
                              }}
                            />
                          </div>
                        </div>
                        {/* Name/Description container - Same style as OUR MISSION & VALUES */}
                        <div 
                          className="group/card relative border-4 border-white/50 text-white p-6 cursor-pointer transition-all duration-300 hover:border-white hover:shadow-lg hover:shadow-white/20"
                          style={{ clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)' }}
                          onClick={() => handleProductClick(product.id)}
                        >
                          {/* Corner decorations */}
                          <div
                            className="pointer-events-none absolute top-0 left-0 bg-white/50 transition-all duration-300 group-hover/card:bg-white"
                            style={{ width: 20, height: 20, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                          />
                          <div
                            className="pointer-events-none absolute bottom-0 right-0 bg-white/50 transition-all duration-300 group-hover/card:bg-white"
                            style={{ width: 20, height: 20, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                          />
                          <Title level={4} className="font-displayed !font-normal text-white uppercase text-center !mb-2">{product.name}</Title>
                          {isAuthenticated && (
                            <div className="font-orator text-orangicide font-bold text-xl mb-2 text-center">
                              {product.variants && product.variants.length > 1 && (
                                <span className="text-zinc-400 text-sm mr-2">starting at</span>
                              )}
                              {formatPrice(getBasePrice(product))}
                            </div>
                          )}
                          {!isAuthenticated && (
                            <Paragraph className="font-orator text-orangicide mb-0 text-center">{product.category || ''}</Paragraph>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </main>

        {/* 5. Seção Final "Need a Quote?" */}
        <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat py-20 relative">
          <div className="container mx-auto px-4 text-center">
            <div className="flex justify-center items-center gap-4 md:gap-6 mb-12">
            <img 
                  src="/assets/frames/need-help-frame-faded.svg"
                  alt="Need Help Frame"
                  className="max-w-full md:max-w-5xl h-auto object-cover lg:mt-16 [mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_10%,black_15%,black_85%,transparent_90%)]"
                />
            </div>

            <Title level={1} className="font-displayed !font-normal text-white uppercase !mb-0">
              NEED HELP FINDING THE RIGHT PRODUCT?
            </Title>
            <Paragraph className="font-orator text-orangicide max-w-3xl tracking-wider mx-auto mt-6 !mb-12 !text-xl lg:!text-2xl uppercase">
              OUR TEAM IS AVAILABLE TO ASSIST YOU IN SELECTING THE PERFECT GEAR FOR YOUR MACHINES.
            </Paragraph>
            <button onClick={() => setIsContactModalVisible(true)} className="btn-orangicide font-displayed text-xl md:text-2xl !w-auto px-8 py-4 tracking-wider cursor-pointer uppercase">
              CONTACT OUR EXPERTS
            </button>
          </div>
        </section>

        {/* 6 - Our Family of Brands Section */}
        <section className="bg-zinc-950 pb-12">
          <div className="container mx-auto">
            <div className="font-displayed !font-normal text-white text-center text-4xl md:text-5xl uppercase py-12">
              OUR FAMILY OF BRANDS
            </div>
            
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 pb-6">
              {/* Logos das marcas */}
              <a 
                href="https://ruppertinc.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/assets/logos/RuppertLogo.svg" 
                  alt="Ruppert Logo" 
                  className="max-w-full max-h-full" 
                />
              </a>
              <a 
                href="https://estabrookinc.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/assets/logos/EstabrookLogo.svg" 
                  alt="Estabrook Logo" 
                  className="max-w-full max-h-full" 
                />
              </a>
              <a 
                href="https://modlink.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/assets/logos/ModLinkLogo.svg" 
                  alt="Modlink Logo" 
                  className="max-w-full max-h-full"
                />
              </a>
              <div className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center">
                <img 
                  src="/assets/logos/SnowSyncLogo.svg" 
                  alt="Snow Sync Logo" 
                  className="max-w-full max-h-full" 
                />
              </div>
              <div className="w-[100px] md:w-[150px] h-[70px] md:mt-2 flex items-center justify-center">
                <img 
                  src="/assets/logos/asrLogo.svg" 
                  alt="ASR Logo" 
                  className="max-w-full max-h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Contact Experts Modal */}
        <ContactExpertsModal
          visible={isContactModalVisible}
          onClose={() => setIsContactModalVisible(false)}
          source="merch"
        />

        {/* Drawer de Filtros Mobile: agora passa as props */}
        <Drawer
          placement="left"
          onClose={() => setIsFilterDrawerVisible(false)}
          open={isFilterDrawerVisible}
          closeIcon={<span className="text-orangicide text-2xl">X</span>}
          styles={{
            header: { backgroundColor: '#181717', borderBottom: '1px solid #3f3f46' },
            body: { backgroundColor: '#181717', padding: '24px' },
          }}
        >
          <FilterContent 
            filters={filters}
            onFilterChange={handleFilterChange}
            openFilters={openFilters}
            onToggleFilter={handleToggleFilter}
            filterOptions={filterOptions}
          />
        </Drawer>
      </div>
    </>
  );
};
